import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Article from './Article';
import './ArticleList.css';

const baseURL = "https://warehouse-picker-proxy.qustom.workers.dev/proxy";

const ArticleList = ({ conneoId, batchId }) => {
  const [articles, setArticles] = useState([]);
  const [pickedArticles, setPickedArticles] = useState(() => {
    return JSON.parse(localStorage.getItem('pickedArticles')) || {};
  });
  const [globalAvailableStock, setGlobalAvailableStock] = useState({}); // Global stock per locationId
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false); // New state to track if processing is in progress
  const [hasProcessed, setHasProcessed] = useState(false); // Track if Process All has been used
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (conneoId && batchId) {
      const URL = `${baseURL}/${conneoId}/${batchId}?addPicked=1`;

      fetch(URL)
        .then(response => {
          if (!response.ok) {
            if(response.status === 403) throw new Error(`Forbidden access! please provide a valid id`);
            throw new Error(`Network response was not ok: ${response.status}`);
          }
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.indexOf('application/json') !== -1) {
            return response.json();
          } else {
            return response.text().then(text => {
              throw new Error(`Expected JSON, got: ${text}`);
            });
          }
        })
        .then(data => {
          if (data.meta.status === "error") {
            setArticles([]); // Clear articles if there is an error
          } else {
            setArticles(data.data.items);

            // Initialize global available stock for all locations across all articles
            const stockMap = {};
            data.data.items.forEach(article => {
              // Add main location - use paperid + locationId as key
              if (article.locationId && article.available !== undefined && article.paperid) {
                const key = `${article.paperid}_${article.locationId}`;
                stockMap[key] = article.available;
              }
              // Add alternative locations
              if (article.locations && article.paperid) {
                article.locations.forEach(location => {
                  if (location.locationId && location.available !== undefined) {
                    // Use paperid + locationId as key
                    const key = `${article.paperid}_${location.locationId}`;
                    // Use the maximum available if location appears multiple times
                    stockMap[key] = Math.max(
                      stockMap[key] || 0,
                      location.available
                    );
                  }
                });
              }
            });
            setGlobalAvailableStock(stockMap);
          }
        })
        .catch(error => {
          setError(error.message);
        });
    }
  }, [conneoId, batchId]);

  const handleProcess = async () => {
    // Early return if already processing or has been processed
    if (isProcessing || hasProcessed) {
      alert('Deze actie is al uitgevoerd of wordt verwerkt. Wacht tot de verwerking is voltooid.');
      return;
    }

    // Check if all articles are fully picked (each order line separately)
    const notFullyPicked = articles.filter(article => {
      const key = `${article.id}_${article.orderNumber}`;
      const pickedQty = pickedArticles[key]?.mainQuantity || 0;
      const altLocQty = Object.values(pickedArticles[key]?.locations || {}).reduce((sum, qty) => sum + qty, 0);
      const totalPicked = pickedQty + altLocQty;
      return totalPicked < article.qty;
    });

    // If not everything is picked, show confirmation modal
    if (notFullyPicked.length > 0) {
      setShowConfirmModal(true);
      return; // Wait for user confirmation
    }

    // If everything is picked, proceed directly
    proceedWithProcessing();
  };

  const proceedWithProcessing = async () => {
    setShowConfirmModal(false);
    setIsProcessing(true); // Disable the button when the process starts
    setHasProcessed(true); // Mark as processed to prevent duplicate submissions

    const items = Object.entries(pickedArticles).map(([key, { mainQuantity, locations }]) => {
      // Parse key: "id_orderNumber"
      const [id, orderNumber] = key.split('_');
      const article = articles.find(article => article.id === parseInt(id) && article.orderNumber === orderNumber);
      if (!article) {
        console.error(`Article with key ${key} not found`);
        return null; // Skip this item if the article is not found
      }

      return {
        orderNumber: article.orderNumber,
        id: parseInt(id),
        job: article.job,
        locations: [
          {
            pickQuantity: mainQuantity,
            locationId: article.locationId
          },
          ...Object.entries(locations || {}).map(([locationId, pickQuantity]) => ({
            pickQuantity,
            locationId: parseInt(locationId)
          }))
        ].filter(location => location.pickQuantity > 0)
      };
    }).filter(item => item !== null); // Remove any null items

    if (items.length === 0) {
      setError('No quantities picked to process.');
      setIsProcessing(false); // Re-enable the button if no items to process
      return;
    }

    const payload = {
      items,
      picklist: {
        key: batchId,
        code: ""
      }
    };

    console.log(JSON.stringify(payload));
    
    try {
      const response = await fetch(`${baseURL}/${conneoId}/${batchId}`, {

        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      console.log(JSON.stringify(payload))
      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }
      const data = await response.json();
      if (data.data.status === 'ok') {
        alert('Data processed successfully!');
        localStorage.removeItem('pickedArticles');
        setPickedArticles({});
        window.location.reload();
       // navigate(`/${conneoId}/${batchId}`); // Redirect to /conneoId after successful processing
      } else {
        alert(data.data);
        setIsProcessing(false); // Re-enable the button if processing fails
      }
    } catch (error) {
      setError('Failed to process the picked quantities. Please try again.');
      setIsProcessing(false); // Re-enable the button if an error occurs
    }
  };

  return (
    <div className="article-list">
      {error && (
        <>
          <p className="error-message">{error}</p>
          <button onClick={() => navigate(`/${conneoId}`)} className="btn-back">
            ← Terug
          </button>
        </>
      )}

      {articles.length === 0 && !error && (
        <>
          <button onClick={() => navigate(`/${conneoId}`)} className="btn-back">
            ← Terug
          </button>
        </>
      )}

      {articles.map((article, index) => (
        <Article
          key={`${article.id}-${index}`}
          article={article}
          pickedArticles={pickedArticles}
          setPickedArticles={setPickedArticles}
          conneoId={conneoId}
          batchId={batchId}
          globalAvailableStock={globalAvailableStock}
          setGlobalAvailableStock={setGlobalAvailableStock}
        />
      ))}

      {articles.length > 0 && (
        <div className="button-group-single">
          <button
            onClick={handleProcess}
            className="btn-process"
            disabled={isProcessing || hasProcessed || Object.keys(pickedArticles).length === 0} // Disable if processing, processed, or nothing picked
          >
            {hasProcessed ? 'Verwerkt ✓' : isProcessing ? 'Processing...' : 'Process All'}
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '10px',
            maxWidth: '400px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '15px' }}>⚠️</div>
            <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Let op!</h3>
            <p style={{ color: '#666', marginBottom: '25px', lineHeight: '1.5' }}>
              Nog niet alles is gepakt.
              <br /><br />
              Weet je zeker dat je wilt doorgaan met verwerken?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Annuleren
              </button>
              <button
                onClick={proceedWithProcessing}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  backgroundColor: '#28a745',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Doorgaan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticleList;
